import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, CheckCircle, Circle, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

type StripeConnectStatus = {
  connected: boolean;
  accountId: string | null;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  connectedAt: string | null;
  platformFeePercent: number;
};

const StatusRow = ({ label, value }: { label: string; value: boolean }) => (
  <div className="flex items-center gap-2">
    {value ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <Circle className="h-4 w-4 text-amber-500" />
    )}
    <span>{label}</span>
  </div>
);

export default function PaymentsSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const fetchedRef = useRef(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const data = await apiClient<StripeConnectStatus>('/admin/stripe/status');
      setStatus(data);
    } catch {
      toast.error(t('admin.payments_fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchStatus();

    // Detect return from Stripe onboarding
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarded') === 'true' || params.get('refresh') === 'true') {
      setTimeout(fetchStatus, 2000); // give webhook time to update
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, []);

  const handleConnect = async () => {
    setOnboarding(true);
    try {
      const resp = await apiClient<{ onboardingUrl: string }>('/admin/stripe/onboard', {
        method: 'POST',
      });
      window.location.href = resp.onboardingUrl;
    } catch {
      toast.error(t('admin.payments_connect_error'));
      setOnboarding(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const resp = await apiClient<{ url: string }>('/admin/stripe/dashboard-link', {
        method: 'POST',
      });
      window.open(resp.url, '_blank');
    } catch {
      toast.error(t('admin.payments_dashboard_error'));
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader className="pb-2">
        <h2 className="text-sm font-semibold">{t('admin.payments_title')}</h2>
        <p className="text-xs text-muted-foreground">{t('admin.payments_subtitle')}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Not connected */}
        {!status?.connected && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h4 className="font-semibold mb-2">{t('admin.payments_not_connected')}</h4>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              {t('admin.payments_not_connected_desc')}
            </p>
            <Button onClick={handleConnect} disabled={onboarding}>
              {onboarding ? t('admin.payments_redirecting') : t('admin.payments_connect_btn')}
            </Button>
          </div>
        )}

        {/* Connected but incomplete */}
        {status?.connected && !status.onboardingComplete && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900">{t('admin.payments_incomplete')}</h4>
                <p className="text-sm text-amber-800 mt-1 mb-4">{t('admin.payments_incomplete_desc')}</p>
                <div className="space-y-2 text-sm text-amber-800 mb-4">
                  <StatusRow label={t('admin.payments_details_submitted')} value={status.detailsSubmitted} />
                  <StatusRow label={t('admin.payments_charges_enabled')} value={status.chargesEnabled} />
                  <StatusRow label={t('admin.payments_payouts_enabled')} value={status.payoutsEnabled} />
                </div>
                <Button onClick={handleConnect} variant="outline">{t('admin.payments_continue')}</Button>
              </div>
            </div>
          </div>
        )}

        {/* Fully connected */}
        {status?.onboardingComplete && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-green-900">{t('admin.payments_connected')}</h4>
                <p className="text-sm text-green-800 mt-1 mb-4">
                  {t('admin.payments_connected_desc')}
                </p>
                <Button onClick={handleOpenDashboard} variant="outline">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('admin.payments_open_dashboard')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Fee disclosure */}
        {status?.connected && (
          <div className="rounded-lg border bg-muted/30 p-4">
            <h5 className="font-semibold text-sm mb-2">{t('admin.payments_fee_title')}</h5>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• {t('admin.payments_platform_fee', { percent: status.platformFeePercent })}</li>
              <li>• {t('admin.payments_stripe_fees')}</li>
              <li>• {t('admin.payments_payout_schedule')}</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
